import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Alert, AlertDescription } from '../ui/alert';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { Product, productService, CheckStockResponse } from '../../services/product';
import { ProductProcessingModal } from './ProductProcessingModal';
import {
  ArrowLeft,
  Package,
  Calendar,
  Store,
  Tag,
  Image as ImageIcon,
  Loader2,
  ExternalLink,
  Database,
  Sparkles,
  CheckCircle2,
  Clock,
  RotateCcw
} from 'lucide-react';

export function ProductDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [isProcessingModalOpen, setIsProcessingModalOpen] = useState(false);
  const [isCheckingStock, setIsCheckingStock] = useState(false);
  const [stockResult, setStockResult] = useState<CheckStockResponse | null>(null);
  const [isResettingTransformations, setIsResettingTransformations] = useState(false);
  const [isRollingBack, setIsRollingBack] = useState(false);
  const [showResetConfirmDialog, setShowResetConfirmDialog] = useState(false);

  // Get the return path from navigation state, fallback to products list
  const getReturnPath = () => {
    const state = location.state as { returnTo?: string; from?: string } | null;
    return state?.returnTo || '/products';
  };

  const handleBackNavigation = () => {
    const returnPath = getReturnPath();
    navigate(returnPath);
  };

  // Extract readable info about where user will return to
  const getReturnInfo = () => {
    const returnPath = getReturnPath();
    const url = new URL(returnPath, window.location.origin);
    const params = new URLSearchParams(url.search);
    
    // Check if returning to bulk-review page
    if (returnPath === '/products/bulk-review') {
      return 'Bulk Review';
    }
    
    const page = params.get('page');
    const search = params.get('search');
    const category = params.get('category');
    
    let info = 'Products';
    if (page && page !== '1') info += ` (page ${page})`;
    if (search) info += ` - "${search}"`;
    if (category) info += ` - ${category}`;
    
    return info;
  };

  useEffect(() => {
    if (id) {
      loadProduct(id);
    }
  }, [id]);

  const loadProduct = async (productId: string) => {
    try {
      setLoading(true);
      setError(null);
      const product = await productService.getProduct(productId);
      setProduct(product);
      
      // Set the first available image as selected
      if (product.main_image_url) {
        setSelectedImage(product.main_image_url);
      } else if (product.gallery_images && product.gallery_images.length > 0) {
        // Handle both string URLs and objects with url property
        const firstImage = product.gallery_images[0];
        setSelectedImage(typeof firstImage === 'string' ? firstImage : firstImage.url);
      }
    } catch (error: any) {
      setError(error.message || 'Failed to load product');
    } finally {
      setLoading(false);
    }
  };

  const getAllImages = (): string[] => {
    const images: string[] = [];

    if (product?.main_image_url) {
      images.push(product.main_image_url);
    }

    if (product?.gallery_images && Array.isArray(product.gallery_images)) {
      product.gallery_images.forEach((img: any) => {
        // Handle both string URLs and objects with url property
        if (typeof img === 'string') {
          if (!images.includes(img)) {
            images.push(img);
          }
        } else if (img && img.url && !images.includes(img.url)) {
          images.push(img.url);
        }
      });
    }

    return images;
  };

  // Get image metadata for a specific URL
  const getImageMetadata = (imageUrl: string): any => {
    if (!product?.gallery_images || !Array.isArray(product.gallery_images)) {
      return null;
    }

    for (const img of product.gallery_images) {
      if (typeof img === 'object' && img.url === imageUrl) {
        return img;
      }
    }

    return null;
  };

  // Format Magento image role for display
  const formatImageRole = (role: string): string => {
    const roleMap: Record<string, string> = {
      'image': 'Main',
      'small_image': 'Small',
      'thumbnail': 'Thumb',
      'swatch_image': 'Swatch',
      'base': 'Base'
    };
    return roleMap[role] || role;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleProcessProduct = () => {
    setIsProcessingModalOpen(true);
  };

  const handleJobCreated = (jobId: string, isAsync?: boolean) => {
    setIsProcessingModalOpen(false);
    // Navigate to batch status page to monitor processing
    if (isAsync) {
      navigate(`/jobs/async-monitor?job_id=${jobId}`);
    } else {
      navigate(`/jobs/batch/${jobId}`);
    }
    console.log(`Job created: ${jobId}, async: ${isAsync}`);
  };

  const handleCheckStock = async () => {
    if (!product) return;

    setIsCheckingStock(true);
    setStockResult(null);

    try {
      const result = await productService.checkStock(product.id, 'online_wh');
      setStockResult(result);

      // Update the product state with new stock data
      setProduct({
        ...product,
        stock_quantity: result.stock_quantity,
        stock_source_code: result.stock_source_code
      });
    } catch (error: any) {
      console.error('Failed to check stock:', error);
      setStockResult({
        stock_quantity: null,
        stock_source_code: 'online_wh',
        stock_status: 'unknown',
        sku: product.sku
      });
    } finally {
      setIsCheckingStock(false);
    }
  };

  const handleResetTransformations = () => {
    if (!product) return;
    setShowResetConfirmDialog(true);
  };

  const confirmResetStatusOnly = async () => {
    if (!product) return;

    setShowResetConfirmDialog(false);
    setIsResettingTransformations(true);

    try {
      await productService.resetProductTransformations(product.id);

      // Reload product to get updated transformation status
      await loadProduct(product.id);
    } catch (error: any) {
      console.error('Failed to reset transformations:', error);
      setError(error.message || 'Failed to reset transformations');
    } finally {
      setIsResettingTransformations(false);
    }
  };

  const confirmRollbackToOriginal = async () => {
    if (!product) return;

    setShowResetConfirmDialog(false);
    setIsRollingBack(true);

    try {
      await productService.rollbackProductTransformations(product.id);

      // Reload product to get updated state
      await loadProduct(product.id);
    } catch (error: any) {
      console.error('Failed to rollback transformations:', error);
      setError(error.message || 'Failed to rollback to original images');
    } finally {
      setIsRollingBack(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-8 animate-pulse">
        {/* Header skeleton */}
        <div className="flex items-center justify-between">
          <div className="h-10 bg-muted rounded w-32"></div>
          <div className="flex gap-3">
            <div className="h-10 bg-muted rounded w-32"></div>
            <div className="h-10 bg-muted rounded w-32"></div>
          </div>
        </div>
        
        {/* Content skeleton */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Image section skeleton */}
          <div className="space-y-4">
            <div className="aspect-square bg-muted rounded-xl"></div>
            <div className="grid grid-cols-4 gap-2">
              {Array(4).fill(0).map((_, i) => (
                <div key={i} className="aspect-square bg-muted rounded-lg"></div>
              ))}
            </div>
          </div>
          
          {/* Info section skeleton */}
          <div className="space-y-6">
            <div className="h-32 bg-muted rounded-xl"></div>
            <div className="h-24 bg-muted rounded-xl"></div>
            <div className="h-20 bg-muted rounded-xl"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <Button 
          variant="outline" 
          onClick={handleBackNavigation}
          className="gap-2"
          title={`Return to: ${getReturnInfo()}`}
        >
          <ArrowLeft className="h-4 w-4" />
          Back to {getReturnInfo()}
        </Button>
        <Alert className="border-destructive bg-destructive/5">
          <AlertDescription className="text-destructive font-medium">{error}</AlertDescription>
        </Alert>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="space-y-6">
        <Button 
          variant="outline" 
          onClick={handleBackNavigation}
          className="gap-2"
          title={`Return to: ${getReturnInfo()}`}
        >
          <ArrowLeft className="h-4 w-4" />
          Back to {getReturnInfo()}
        </Button>
        <Alert className="bg-muted/50">
          <AlertDescription>Product not found</AlertDescription>
        </Alert>
      </div>
    );
  }

  const allImages = getAllImages();
  const hasImages = allImages.length > 0;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <Button 
            variant="outline" 
            onClick={handleBackNavigation}
            className="gap-2 mb-3"
            title={`Return to: ${getReturnInfo()}`}
          >
            <ArrowLeft className="h-4 w-4" />
            Back to {getReturnInfo()}
          </Button>
          <h1 className="text-3xl font-bold tracking-tight line-clamp-1">{product.name}</h1>
          <p className="text-muted-foreground">
            Product details and image management
          </p>
        </div>
        
        <div className="flex gap-3">
          <Button 
            variant="outline"
            disabled={!hasImages}
            className="gap-2 shadow-md"
            onClick={handleProcessProduct}
          >
            <ImageIcon className="h-4 w-4" />
            Process Images
          </Button>
          {product.stores?.url && (
            <Button 
              variant="outline"
              onClick={() => window.open(`${product.stores!.url}/catalog/product/view/id/${product.magento_id}`, '_blank')}
              className="gap-2"
            >
              <ExternalLink className="h-4 w-4" />
              View in Store
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Images Section */}
        <div className="space-y-6">
          <Card className="card-enhanced">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <ImageIcon className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <div className="text-lg font-semibold">Product Images</div>
                  <div className="text-sm text-muted-foreground">{allImages.length} image{allImages.length !== 1 ? 's' : ''} available</div>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {hasImages ? (
                <div className="space-y-6">
                  {/* Main image display */}
                  <div className="relative group">
                    <div className="aspect-square bg-gradient-to-br from-muted/50 to-muted rounded-xl overflow-hidden shadow-lg">
                      <img
                        src={selectedImage || allImages[0]}
                        alt={product.name}
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                      />
                    </div>

                    {/* Image indicator */}
                    <div className="absolute top-4 right-4 bg-black/80 backdrop-blur-sm text-white text-sm px-3 py-1.5 rounded-full">
                      {allImages.indexOf(selectedImage || allImages[0]) + 1} of {allImages.length}
                    </div>

                    {/* Magento image roles/tags */}
                    {(() => {
                      const currentMetadata = getImageMetadata(selectedImage || allImages[0]);
                      const currentTypes = currentMetadata?.types || [];
                      const isCurrentTransformed = currentMetadata?.transformed === true;
                      const currentImageTransformations = currentMetadata?.transformations_applied || [];

                      return (
                        <div className="absolute top-4 left-4 flex flex-wrap gap-2">
                          {/* Transformed badge */}
                          {isCurrentTransformed && (
                            <Badge
                              variant="secondary"
                              className="text-xs px-2.5 py-1 bg-green-500 text-white border-none backdrop-blur-sm font-medium"
                              title={`Transformations: ${currentImageTransformations.join(', ')}`}
                            >
                              <Sparkles className="h-3 w-3 mr-1" />
                              Transformed
                            </Badge>
                          )}
                          {/* Image roles */}
                          {currentTypes.map((type: string, index: number) => (
                            <Badge
                              key={index}
                              variant="secondary"
                              className="text-xs px-2.5 py-1 bg-black/80 text-white border-none backdrop-blur-sm font-medium"
                            >
                              {formatImageRole(type)}
                            </Badge>
                          ))}
                        </div>
                      );
                    })()}

                    {/* Position indicator */}
                    {(() => {
                      const currentMetadata = getImageMetadata(selectedImage || allImages[0]);
                      if (currentMetadata?.position !== undefined) {
                        return (
                          <div className="absolute bottom-4 right-4 bg-black/80 backdrop-blur-sm text-white text-xs px-3 py-1.5 rounded-full font-medium">
                            Position: {currentMetadata.position}
                          </div>
                        );
                      }
                      return null;
                    })()}
                  </div>
                  
                  {/* Thumbnail gallery */}
                  {allImages.length > 1 && (
                    <div className="space-y-3">
                      <h4 className="text-sm font-medium text-muted-foreground">Gallery</h4>
                      <div className="grid grid-cols-4 gap-3">
                        {allImages.map((image, index) => {
                          const metadata = getImageMetadata(image);
                          const types = metadata?.types || [];
                          const isTransformed = metadata?.transformed === true;
                          const imageTransformations = metadata?.transformations_applied || [];

                          return (
                            <button
                              key={index}
                              onClick={() => setSelectedImage(image)}
                              className={`relative group aspect-square bg-muted rounded-lg overflow-hidden border-2 transition-all duration-200 hover:shadow-md ${
                                selectedImage === image
                                  ? 'border-primary ring-2 ring-primary/20'
                                  : 'border-transparent hover:border-primary/30'
                              }`}
                            >
                              <img
                                src={image}
                                alt={`${product.name} - ${index + 1}`}
                                className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-110"
                              />

                              {/* Transformed indicator */}
                              {isTransformed && (
                                <div className="absolute top-1 right-1 bg-green-500 text-white p-1 rounded-full shadow-lg" title={`Transformed: ${imageTransformations.join(', ')}`}>
                                  <Sparkles className="h-3 w-3" />
                                </div>
                              )}

                              {/* Image roles/tags badges */}
                              {types.length > 0 && (
                                <div className="absolute top-2 left-2 right-8 flex flex-wrap gap-1">
                                  {types.map((type: string, typeIndex: number) => (
                                    <Badge
                                      key={typeIndex}
                                      variant="secondary"
                                      className="text-[10px] px-1.5 py-0 h-5 bg-black/70 text-white border-none backdrop-blur-sm"
                                    >
                                      {formatImageRole(type)}
                                    </Badge>
                                  ))}
                                </div>
                              )}

                              {/* Position badge */}
                              {metadata?.position !== undefined && (
                                <div className="absolute bottom-2 right-2 bg-black/70 text-white text-[10px] px-2 py-0.5 rounded backdrop-blur-sm">
                                  #{metadata.position}
                                </div>
                              )}

                              {selectedImage === image && (
                                <div className="absolute inset-0 bg-primary/20 flex items-center justify-center">
                                  <div className="w-3 h-3 bg-primary rounded-full"></div>
                                </div>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="aspect-square bg-gradient-to-br from-muted/50 to-muted rounded-xl flex items-center justify-center">
                  <div className="text-center">
                    <div className="p-4 bg-muted/50 rounded-full mb-4 inline-block">
                      <Package className="h-16 w-16 text-muted-foreground" />
                    </div>
                    <p className="text-muted-foreground font-medium">No images available</p>
                    <p className="text-sm text-muted-foreground mt-1">Sync the product to get updated images</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Product Information */}
        <div className="space-y-6">
          {/* Basic Info */}
          <Card className="card-enhanced">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <Package className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <div className="text-lg font-semibold">Product Information</div>
                    <div className="text-sm text-muted-foreground">Basic product details</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {product.status && (
                    <Badge
                      variant={product.status === 'enabled' ? 'default' : 'secondary'}
                      className={product.status === 'enabled' ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white' : ''}
                    >
                      {product.status === 'enabled' ? 'Active' : product.status}
                    </Badge>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCheckStock}
                    disabled={isCheckingStock}
                    className="gap-2"
                  >
                    {isCheckingStock ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Checking...
                      </>
                    ) : (
                      <>
                        <Database className="h-4 w-4" />
                        Check Stock
                      </>
                    )}
                  </Button>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-muted-foreground flex items-center gap-1">
                    <div className="w-1.5 h-1.5 bg-primary rounded-full"></div>
                    SKU
                  </label>
                  <p className="text-sm font-mono bg-muted/50 px-3 py-2 rounded-lg border">{product.sku}</p>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-muted-foreground flex items-center gap-1">
                    <div className="w-1.5 h-1.5 bg-primary rounded-full"></div>
                    Magento ID
                  </label>
                  <p className="text-sm font-mono bg-muted/50 px-3 py-2 rounded-lg border">{product.magento_id}</p>
                </div>
              </div>

              {product.category && (
                <div className="space-y-3">
                  <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <Tag className="h-4 w-4" />
                    Categories
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {product.category.split(',').map((cat, index) => (
                      <Badge key={index} variant="outline" className="font-medium">
                        {cat.trim()}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {product.stores && (
                <div className="space-y-3">
                  <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <Store className="h-4 w-4" />
                    Store
                  </label>
                  <div className="bg-muted/30 p-4 rounded-lg space-y-1">
                    <p className="font-medium">{product.stores.name}</p>
                    <p className="text-sm text-muted-foreground">{product.stores.url}</p>
                  </div>
                </div>
              )}

              {/* Stock Information */}
              {(product.stock_quantity !== null && product.stock_quantity !== undefined) || stockResult ? (
                <div className="space-y-3 pt-4 border-t">
                  <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <Database className="h-4 w-4" />
                    Stock Information
                  </label>
                  <div className="bg-muted/30 p-4 rounded-lg space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Quantity</span>
                      <span className="font-semibold text-lg">
                        {product.stock_quantity !== null && product.stock_quantity !== undefined
                          ? product.stock_quantity
                          : 'N/A'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Status</span>
                      <Badge
                        variant={
                          stockResult?.stock_status === 'in_stock'
                            ? 'default'
                            : stockResult?.stock_status === 'out_of_stock'
                            ? 'destructive'
                            : 'secondary'
                        }
                        className={
                          stockResult?.stock_status === 'in_stock'
                            ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white'
                            : ''
                        }
                      >
                        {stockResult?.stock_status === 'in_stock'
                          ? 'In Stock'
                          : stockResult?.stock_status === 'out_of_stock'
                          ? 'Out of Stock'
                          : stockResult?.stock_status === 'not_found'
                          ? 'Not Found'
                          : product.stock_quantity !== null && product.stock_quantity !== undefined
                          ? product.stock_quantity > 0
                            ? 'In Stock'
                            : 'Out of Stock'
                          : 'Unknown'}
                      </Badge>
                    </div>
                    {product.stock_source_code && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">Source</span>
                        <span className="text-sm font-mono bg-muted px-2 py-1 rounded">
                          {product.stock_source_code}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              ) : null}
            </CardContent>
          </Card>

          {/* Timestamps */}
          <Card className="card-enhanced">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Calendar className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <div className="text-lg font-semibold">Timeline</div>
                  <div className="text-sm text-muted-foreground">Product lifecycle dates</div>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-muted-foreground flex items-center gap-1">
                  <div className="w-1.5 h-1.5 bg-primary rounded-full"></div>
                  Created
                </label>
                <p className="text-sm font-mono bg-muted/50 px-3 py-2 rounded-lg border">{formatDate(product.created_at)}</p>
              </div>
              
              {product.last_synced && (
                <div className="space-y-2">
                  <label className="text-sm font-medium text-muted-foreground flex items-center gap-1">
                    <div className="w-1.5 h-1.5 bg-success rounded-full"></div>
                    Last Synced
                  </label>
                  <p className="text-sm font-mono bg-muted/50 px-3 py-2 rounded-lg border">{formatDate(product.last_synced)}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Transformation Status */}
          <Card className="card-enhanced">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${
                  product.transformation_status === 'complete'
                    ? 'bg-green-500/10'
                    : product.transformation_status === 'partial'
                    ? 'bg-yellow-500/10'
                    : 'bg-primary/10'
                }`}>
                  <Sparkles className={`h-5 w-5 ${
                    product.transformation_status === 'complete'
                      ? 'text-green-500'
                      : product.transformation_status === 'partial'
                      ? 'text-yellow-500'
                      : 'text-primary'
                  }`} />
                </div>
                <div>
                  <div className="text-lg font-semibold">Transformation Status</div>
                  <div className="text-sm text-muted-foreground">Image processing history</div>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Status Badge */}
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Status</span>
                <div className="flex items-center gap-2">
                  <Badge
                    variant={
                      product.transformation_status === 'complete'
                        ? 'default'
                        : product.transformation_status === 'partial'
                        ? 'secondary'
                        : 'outline'
                    }
                    className={
                      product.transformation_status === 'complete'
                        ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white'
                        : product.transformation_status === 'partial'
                        ? 'bg-gradient-to-r from-yellow-500 to-orange-500 text-white'
                        : ''
                    }
                  >
                    {product.transformation_status === 'complete' && <CheckCircle2 className="h-3 w-3 mr-1" />}
                    {product.transformation_status === 'partial' && <Clock className="h-3 w-3 mr-1" />}
                    {product.transformation_status === 'complete'
                      ? 'Fully Transformed'
                      : product.transformation_status === 'partial'
                      ? 'Partially Transformed'
                      : 'Not Transformed'}
                  </Badge>
                  {/* Reset button - only show if product has been transformed */}
                  {(product.transformation_status === 'complete' || product.transformation_status === 'partial') && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleResetTransformations}
                      disabled={isResettingTransformations || isRollingBack}
                      className="h-7 px-2 text-xs"
                      title="Reset transformation status or rollback to original"
                    >
                      {(isResettingTransformations || isRollingBack) ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <RotateCcw className="h-3 w-3" />
                      )}
                    </Button>
                  )}
                </div>
              </div>

              {/* Applied Transformations */}
              {product.applied_transformations && product.applied_transformations.length > 0 && (
                <div className="space-y-3 pt-3 border-t">
                  <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <Sparkles className="h-4 w-4" />
                    Applied Transformations
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {product.applied_transformations.map((transformation, index) => (
                      <Badge key={index} variant="outline" className="font-medium capitalize">
                        {transformation.replace(/_/g, ' ')}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Last Transformed */}
              {product.last_transformed_at && (
                <div className="space-y-2 pt-3 border-t">
                  <label className="text-sm font-medium text-muted-foreground flex items-center gap-1">
                    <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
                    Last Transformed
                  </label>
                  <p className="text-sm font-mono bg-muted/50 px-3 py-2 rounded-lg border">
                    {formatDate(product.last_transformed_at)}
                  </p>
                </div>
              )}

              {/* Image Availability */}
              <div className="pt-3 border-t">
                {hasImages ? (
                  <Alert variant="success">
                    <ImageIcon className="h-4 w-4" />
                    <AlertDescription>
                      <div className="space-y-1">
                        <p className="font-medium">Ready for processing</p>
                        <p className="text-sm">{allImages.length} image{allImages.length !== 1 ? 's' : ''} available for enhancement</p>
                      </div>
                    </AlertDescription>
                  </Alert>
                ) : (
                  <Alert variant="warning">
                    <Package className="h-4 w-4" />
                    <AlertDescription>
                      <div className="space-y-1">
                        <p className="font-medium">No images available</p>
                        <p className="text-sm">Sync the product to get updated images for processing</p>
                      </div>
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
      
      {/* Processing Modal */}
      {product && (
        <ProductProcessingModal
          product={product}
          isOpen={isProcessingModalOpen}
          onClose={() => setIsProcessingModalOpen(false)}
          onJobCreated={handleJobCreated}
        />
      )}

      {/* Reset Transformation Confirmation Dialog */}
      <AlertDialog open={showResetConfirmDialog} onOpenChange={setShowResetConfirmDialog}>
        <AlertDialogContent className="sm:max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-xl">Reset Transformations</AlertDialogTitle>
            <AlertDialogDescription>
              Choose how you want to reset this product's transformations:
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="space-y-3 py-2">
            <div className="border rounded-lg p-4 space-y-2 bg-muted/30">
              <span className="font-semibold text-foreground block">Reset Status Only</span>
              <span className="text-sm text-muted-foreground leading-relaxed block">
                Clear transformation tracking so you can reprocess the current images with different settings.
                The enhanced images in Magento remain unchanged.
              </span>
            </div>

            <div className="border rounded-lg p-4 space-y-2 bg-muted/30">
              <span className="font-semibold text-foreground block">Rollback to Original</span>
              <span className="text-sm text-muted-foreground leading-relaxed block">
                Restore the original images in Magento and remove the enhanced versions.
                Use this to completely undo transformations.
              </span>
            </div>
          </div>

          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel className="sm:flex-1">Cancel</AlertDialogCancel>
            <Button
              variant="outline"
              onClick={confirmResetStatusOnly}
              className="sm:flex-1"
            >
              Reset Status Only
            </Button>
            <Button
              variant="destructive"
              onClick={confirmRollbackToOriginal}
              className="sm:flex-1"
            >
              Rollback to Original
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}